import type {
  InactiveParticipant,
  Participant,
  ParticipantType,
} from '@entities/participants';
import type { ParticipantRepository } from '@infra/repositories/participant.repository';

export class MemParticipantRepository implements ParticipantRepository {
  participantMap: Map<string, Participant[]>;
  inactiveParticipantMap: Map<string, InactiveParticipant>;

  constructor() {
    this.participantMap = new Map();
    this.inactiveParticipantMap = new Map();
  }

  async getParticipantsByCallId(
    callId: string,
  ): Promise<Participant[] | undefined> {
    return this.participantMap.get(callId);
  }

  async getExternalParticipantByCallId(
    callId: string,
  ): Promise<InactiveParticipant | undefined> {
    return this.inactiveParticipantMap.get(callId);
  }

  async clearParticipants(): Promise<void> {
    this.participantMap.clear();
  }

  async clearExternalParticipant(): Promise<void> {
    this.inactiveParticipantMap.clear();
  }

  async addExternalParticipant(
    callId: string,
    participants: InactiveParticipant,
  ): Promise<void> {
    this.inactiveParticipantMap.set(callId, participants);
  }

  async addParticipants(
    callId: string,
    participants: Participant[],
  ): Promise<void> {
    const existingParticipants =
      (await this.getParticipantsByCallId(callId)) || [];

    for (const participant of participants) {
      const existingIndex = existingParticipants.findIndex(
        (p) => p.type === participant.type,
      );

      if (existingIndex >= 0) {
        // Update existing participant with new legId
        existingParticipants[existingIndex] = {
          ...existingParticipants[existingIndex],
          legId: participant.legId,
        };
      } else {
        // Add new participant
        existingParticipants.push(participant);
      }
    }

    this.participantMap.set(callId, existingParticipants);
  }

  async selectParticipantByCallIdAndType(
    callId: string,
    type: ParticipantType,
  ): Promise<Participant> {
    let participants = await this.getParticipantsByCallId(callId);

    if (!participants || participants.length === 0) {
      // If no participants found for callId, try to find from all participants
      const allParticipants = Array.from(this.participantMap.values()).flat();
      participants = allParticipants;
    }

    let selectedParticipant;
    if (participants) {
      selectedParticipant = participants.find((p) => p.type === type);
    }

    if (!selectedParticipant) {
      throw new Error('No participants available');
    }

    return selectedParticipant;
  }
}
